import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Rocket, Sparkles, Send, Bot, Search, Target, Mail, MessageSquare, Check, X, Loader2,
  ExternalLink, Globe, RefreshCw, Eye, Megaphone, ArrowRight, ShieldCheck, Info,
} from "lucide-react";
import { toast } from "sonner";

const FIT_STYLE = {
  excellent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  strong: "bg-violet-50 text-[#7C3AED] border-violet-200",
  moderate: "bg-sky-50 text-sky-700 border-sky-200",
  weak: "bg-amber-50 text-amber-700 border-amber-200",
  poor: "bg-red-50 text-red-700 border-red-200",
};
const SEGMENT_STYLE = {
  high_priority: "bg-emerald-50 text-emerald-700",
  nurture: "bg-amber-50 text-amber-700",
  disqualify: "bg-red-50 text-red-700",
};

const GOAL_FIELDS = [
  ["business_summary", "What you sell"], ["target_industry", "Target industry"],
  ["company_size", "Company size"], ["target_titles", "Target titles"],
  ["geography", "Geography"], ["pain_points", "Pain points"], ["tone", "Outreach tone"],
];

export default function GrowthEngine() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [goals, setGoals] = useState(null);
  const [goalsReady, setGoalsReady] = useState(false);
  const [showChat, setShowChat] = useState(true);
  const [leads, setLeads] = useState([]);
  const [findingLeads, setFindingLeads] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [researching, setResearching] = useState(null);
  const [researchLead, setResearchLead] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [outreach, setOutreach] = useState(null);
  const [approving, setApproving] = useState(false);
  const [result, setResult] = useState(null);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/growth/state").then(({ data }) => {
      setMessages((data.messages || []).map((m) => ({ role: m.role, text: m.text })));
      setGoals(data.goals || null);
      setGoalsReady(!!data.goals_ready);
      setLeads(data.leads || []);
      setShowChat(!data.goals_ready);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, chatLoading]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || chatLoading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setChatLoading(true);
    try {
      const { data } = await api.post("/growth/chat", { message: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.goals) setGoals(data.goals);
      if (data.ready) { setGoalsReady(true); }
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I hit an error. Please try again." }]);
    } finally { setChatLoading(false); }
  };

  const findLeads = async () => {
    setFindingLeads(true);
    setResult(null);
    try {
      const { data } = await api.post("/growth/find-leads");
      setLeads(data.items || []);
      setSelected(new Set());
      setOutreach(null);
      setShowChat(false);
      toast.success(`Found ${data.total} candidate leads from the web`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't find leads right now");
    } finally { setFindingLeads(false); }
  };

  const toggleSelect = (id) => {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const openResearch = async (lead) => {
    if (lead.research) { setResearchLead(lead); return; }
    setResearching(lead.id);
    try {
      const { data } = await api.post(`/growth/leads/${lead.id}/research`);
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? data : l)));
      setResearchLead(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Research failed");
    } finally { setResearching(null); }
  };

  const rejectLead = async (id) => {
    try {
      await api.post(`/growth/leads/${id}/reject`);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status: "rejected" } : l)));
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    } catch { toast.error("Couldn't reject lead"); }
  };

  const draftOutreach = async () => {
    if (selected.size === 0) return toast.error("Select at least one lead first");
    setDrafting(true);
    try {
      const { data } = await api.post("/growth/outreach", { lead_ids: Array.from(selected) });
      setOutreach(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't draft outreach");
    } finally { setDrafting(false); }
  };

  const updateStep = (i, field, value) => {
    setOutreach((o) => { const seq = [...o.email_sequence]; seq[i] = { ...seq[i], [field]: value }; return { ...o, email_sequence: seq }; });
  };

  const approve = async () => {
    setApproving(true);
    try {
      const { data } = await api.post("/growth/approve", {
        lead_ids: Array.from(selected), email_sequence: outreach.email_sequence,
        sms_draft: outreach.sms_draft, sequence_name: `AI Growth: ${goals?.target_industry || "Outreach"}`,
        create_campaign: true,
      });
      setResult(data);
      setOutreach(null);
      setLeads((ls) => ls.map((l) => (selected.has(l.id) ? { ...l, status: "approved" } : l)));
      setSelected(new Set());
      toast.success("Campaign created! Review it in Sequences & Campaigns.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create campaign");
    } finally { setApproving(false); }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const step = outreach.email_sequence[0];
      await api.post("/growth/send-test-email", { to: testEmailTo, subject: step.subject, html: step.body });
      toast.success(`Test email sent to ${testEmailTo}`);
      setTestEmailOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send test email");
    } finally { setSendingTest(false); }
  };

  const resetEngine = async () => {
    if (!window.confirm("Start over? This clears your goals, chat, and suggested leads.")) return;
    await api.post("/growth/reset");
    setMessages([]); setGoals(null); setGoalsReady(false); setLeads([]); setSelected(new Set());
    setOutreach(null); setResult(null); setShowChat(true);
  };

  const visibleLeads = leads.filter((l) => l.status !== "rejected" && l.status !== "approved");

  return (
    <div className="space-y-6" data-testid="growth-engine-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl aurelo-gradient flex items-center justify-center shrink-0"><Rocket className="h-5.5 w-5.5 text-white" /></div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Growth Engine</h1>
            <p className="text-sm text-slate-500">Describe your ideal customer — AI finds real leads, researches them, and drafts outreach for your approval.</p>
          </div>
        </div>
        {(goals || leads.length > 0) && (
          <Button data-testid="growth-reset-button" variant="ghost" size="sm" className="text-slate-400 shrink-0" onClick={resetEngine}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Start Over
          </Button>
        )}
      </div>

      {/* Goals summary */}
      {goals && (
        <div data-testid="growth-goals-summary" className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Target className="h-4 w-4 text-[#7C3AED]" />Your Goals & ICP</h3>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowChat((v) => !v)}>
              {showChat ? "Hide chat" : "Edit in chat"}<ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {GOAL_FIELDS.map(([key, label]) => goals[key] ? (
              <div key={key} className="rounded-xl bg-slate-50 p-3">
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{label}</div>
                <div className="text-sm text-slate-700 mt-0.5 line-clamp-2">{goals[key]}</div>
              </div>
            ) : null)}
          </div>
          {goalsReady && (
            <Button data-testid="find-leads-button" className="aurelo-gradient text-white mt-4" onClick={findLeads} disabled={findingLeads}>
              {findingLeads ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}
              {findingLeads ? "Searching the web…" : "Find Leads"}
            </Button>
          )}
        </div>
      )}

      {/* Chat */}
      {showChat && (
        <div className="rounded-2xl bg-white border border-slate-200 flex flex-col h-[480px]" data-testid="growth-chat-panel">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="h-14 w-14 rounded-2xl aurelo-gradient flex items-center justify-center mb-4"><Sparkles className="h-7 w-7 text-white" /></div>
                <h3 className="font-semibold text-slate-900">Tell me about your business and your ideal customer</h3>
                <p className="text-sm text-slate-500 mt-1 max-w-md">For example: "We sell DevOps tooling to engineering teams at Series B+ startups. I'm looking for VP Engineering or CTO at companies with 100-1000 employees in North America."</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={m.role === "user" ? "max-w-[80%] rounded-2xl rounded-br-md aurelo-gradient text-white px-4 py-2.5 text-sm" : "max-w-[85%] rounded-2xl rounded-bl-md bg-slate-50 border border-slate-200 px-4 py-3"}>
                  {m.role === "assistant" && <div className="flex items-center gap-1.5 mb-1 text-[11px] font-semibold text-[#7C3AED]"><Bot className="h-3.5 w-3.5" />Growth Engine</div>}
                  <div className={m.role === "user" ? "whitespace-pre-wrap" : "text-sm text-slate-700 whitespace-pre-wrap"}>{m.text}</div>
                </div>
              </div>
            ))}
            {chatLoading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" />Thinking…</div>}
          </div>
          <div className="p-3 border-t border-slate-100 flex items-end gap-2">
            <Textarea data-testid="growth-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              rows={1} placeholder="Describe your business and ideal customer…" className="flex-1 resize-none max-h-28" />
            <Button data-testid="growth-chat-send" size="icon" onClick={() => sendMessage()} disabled={chatLoading || !input.trim()} className="aurelo-gradient text-white shrink-0"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {/* Candidate leads */}
      {visibleLeads.length > 0 && (
        <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="growth-leads-section">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Globe className="h-4 w-4 text-[#7C3AED]" />Candidate Leads ({visibleLeads.length})</h3>
            {selected.size > 0 && (
              <Button data-testid="draft-outreach-button" size="sm" className="aurelo-gradient text-white" onClick={draftOutreach} disabled={drafting}>
                {drafting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
                Draft Outreach for {selected.size}
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-400 mb-4 flex items-center gap-1"><Info className="h-3 w-3" />Sourced from public web search and scored by Jev — always verify before outreach.</p>
          <div className="space-y-2">
            {visibleLeads.map((l) => (
              <div key={l.id} data-testid={`growth-lead-${l.id}`} className="flex items-start gap-3 rounded-xl border border-slate-200 p-3.5 hover:bg-slate-50/60 transition-colors">
                <Checkbox data-testid={`growth-lead-select-${l.id}`} checked={selected.has(l.id)} onCheckedChange={() => toggleSelect(l.id)} className="mt-1" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">{l.name}</span>
                    {l.title && <span className="text-xs text-slate-400">{l.title}</span>}
                    {l.company_name && <span className="text-xs text-slate-500">· {l.company_name}</span>}
                    <Badge variant="outline" className={`text-[10px] font-semibold ${FIT_STYLE[l.fit_label] || "bg-slate-100 text-slate-600"}`}>{l.fit_label} fit · {l.fit_score}</Badge>
                    <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${SEGMENT_STYLE[l.segment] || "bg-slate-100 text-slate-500"}`}>{(l.segment || "").replace("_", " ")}</span>
                    {!l.ai_classified && <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-400">fallback score</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{l.match_reason}</p>
                  <a href={l.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#7C3AED] hover:underline inline-flex items-center gap-1 mt-1"><ExternalLink className="h-3 w-3" />{l.source_url}</a>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button data-testid={`growth-lead-research-${l.id}`} variant="outline" size="sm" className="text-xs" onClick={() => openResearch(l)} disabled={researching === l.id}>
                    {researching === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                    {l.research ? "View Research" : "Research"}
                  </Button>
                  <Button data-testid={`growth-lead-reject-${l.id}`} variant="ghost" size="sm" className="text-slate-400 hover:text-red-600" onClick={() => rejectLead(l.id)}><X className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Outreach review */}
      {outreach && (
        <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="growth-outreach-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Mail className="h-4 w-4 text-[#7C3AED]" />Review Outreach Draft</h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => { setTestEmailTo(user?.email || ""); setTestEmailOpen(true); }}><Send className="h-3.5 w-3.5 mr-1" />Send Test Email</Button>
            </div>
          </div>
          <div className="space-y-4">
            {outreach.email_sequence.map((s, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-900">Step {i + 1}: {s.name} <span className="text-slate-400 font-normal">(Day {s.delay_days})</span></span>
                </div>
                <Label className="text-[11px] text-slate-400">Subject</Label>
                <Input data-testid={`outreach-subject-${i}`} value={s.subject} onChange={(e) => updateStep(i, "subject", e.target.value)} className="mt-1 mb-2 text-sm" />
                <Label className="text-[11px] text-slate-400">Body</Label>
                <Textarea data-testid={`outreach-body-${i}`} value={s.body} onChange={(e) => updateStep(i, "body", e.target.value)} rows={5} className="mt-1 text-sm" />
              </div>
            ))}
            {outreach.sms_draft && (
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-900 mb-2"><MessageSquare className="h-3.5 w-3.5" />SMS / Text Draft</div>
                <Textarea data-testid="outreach-sms" value={outreach.sms_draft} onChange={(e) => setOutreach((o) => ({ ...o, sms_draft: e.target.value }))} rows={2} className="text-sm" />
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-400 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Nothing is sent automatically — approving creates a draft Sequence & Campaign for your review.</p>
            <Button data-testid="approve-outreach-button" className="aurelo-gradient text-white" onClick={approve} disabled={approving}>
              {approving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
              Approve & Create Campaign
            </Button>
          </div>
        </div>
      )}

      {/* Success */}
      {result && (
        <div data-testid="growth-success-panel" className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0"><Check className="h-5 w-5 text-white" /></div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Created {result.leads.length} lead{result.leads.length === 1 ? "" : "s"}, 1 sequence{result.campaign ? " & 1 campaign" : ""} as drafts</div>
              <p className="text-xs text-slate-500 mt-0.5">Review and launch them whenever you're ready.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/app/leads")}><Target className="h-3.5 w-3.5 mr-1" />View Leads</Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/app/sequences")}><Send className="h-3.5 w-3.5 mr-1" />View Sequence</Button>
            {result.campaign && <Button variant="outline" size="sm" onClick={() => navigate("/app/campaigns")}><Megaphone className="h-3.5 w-3.5 mr-1" />View Campaign</Button>}
          </div>
        </div>
      )}

      <Dialog open={!!researchLead} onOpenChange={(o) => !o && setResearchLead(null)}>
        <DialogContent className="max-w-lg" data-testid="growth-research-dialog">
          <DialogHeader>
            <DialogTitle>{researchLead?.name}</DialogTitle>
            <DialogDescription>{researchLead?.title} {researchLead?.company_name && `· ${researchLead.company_name}`}</DialogDescription>
          </DialogHeader>
          {researchLead?.research && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto text-sm">
              <p className="text-slate-700">{researchLead.research.summary}</p>
              <div>
                <div className="text-xs font-semibold text-slate-900 mb-1">Talking points</div>
                <ul className="list-disc list-inside space-y-1 text-slate-600 text-xs">
                  {(researchLead.research.talking_points || []).map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
              <div className="rounded-xl bg-violet-50 p-3">
                <div className="text-xs font-semibold text-[#7C3AED] mb-1">Suggested icebreaker</div>
                <p className="text-xs text-slate-700">{researchLead.research.suggested_icebreaker}</p>
              </div>
              <p className="text-[11px] text-slate-400 flex items-center gap-1"><Info className="h-3 w-3" />Based on public web search — not independently verified.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={testEmailOpen} onOpenChange={setTestEmailOpen}>
        <DialogContent data-testid="growth-test-email-dialog">
          <DialogHeader><DialogTitle>Send Test Email</DialogTitle><DialogDescription>Sends the first outreach step to an address of your choice via SendGrid.</DialogDescription></DialogHeader>
          <Input data-testid="growth-test-email-input" value={testEmailTo} onChange={(e) => setTestEmailTo(e.target.value)} placeholder="you@example.com" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailOpen(false)}>Cancel</Button>
            <Button data-testid="growth-test-email-send" className="aurelo-gradient text-white" onClick={sendTest} disabled={sendingTest || !testEmailTo}>
              {sendingTest ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
